/**
 * SDQ analysis-result helpers.
 *
 * Port of the guardian app's `src/lib/sdq-risk-thresholds.ts` and
 * `src/lib/sdq-analysis-format.ts` so the admin renders exactly the same report
 * from the same `analysis_content.analysis_results` payload.
 *
 * Two report formats exist side by side:
 *  - legacy: only `evaluation.summary` + `child_behavior_issues`
 *  - new:    `analysis_summary` + `risk_levels` (+ `thresholds` for the radar)
 * Keep both paths working; old reports must keep rendering as they always did.
 */

/** SDQ subscale order on the radar chart (matches the UI labels). */
export const SDQ_SUBSCALE_ORDER = ['PB', 'PP', 'HI', 'ES', 'CP'];

/** Which of the three report layouts the risk levels ask for. */
export const SDQ_REPORT_DISPLAY_CASE = {
  MIXED: 'mixed',
  ALL_LOW: 'allLow',
  BR07: 'br07',
};

const MAX_RISK_SECTION_BEHAVIORS = 3;

/* ------------------------------------------------------------------ *
 * thresholds / scores
 * ------------------------------------------------------------------ */

const isValidSubscaleThreshold = (value) => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return value.high !== null && value.high !== undefined && value.moderate !== null && value.moderate !== undefined;
};

/** Use API thresholds only. Returns null when missing or incomplete - the radar then skips its risk lines. */
export const resolveSdqThresholds = (apiThresholds) => {
  if (!apiThresholds) {
    return null;
  }

  const resolved = {};

  for (const key of SDQ_SUBSCALE_ORDER) {
    const fromApi = apiThresholds[key];
    if (!isValidSubscaleThreshold(fromApi)) {
      return null;
    }
    resolved[key] = { high: fromApi.high, moderate: fromApi.moderate };
  }

  return resolved;
};

export const getScoreFromSummary = (summary, key) => {
  if (!summary) {
    return 0;
  }

  const directScore = summary[key];
  if (directScore !== null && directScore !== undefined) {
    return directScore;
  }

  const matchedEntry = Object.entries(summary).find(([subscaleKey]) => subscaleKey.toUpperCase() === key);

  return matchedEntry?.[1] ?? 0;
};

export const buildRadarRiskLineValues = (thresholds) => ({
  high: SDQ_SUBSCALE_ORDER.map((key) => thresholds[key].high),
  moderate: SDQ_SUBSCALE_ORDER.map((key) => thresholds[key].moderate),
});

/* ------------------------------------------------------------------ *
 * risk levels
 * ------------------------------------------------------------------ */

const getSubscaleRiskLevel = (riskLevels, subscaleKey) => {
  if (!riskLevels) {
    return undefined;
  }

  const directLevel = riskLevels[subscaleKey];
  if (directLevel !== null && directLevel !== undefined) {
    return directLevel;
  }

  const matchedEntry = Object.entries(riskLevels).find(([key]) => key.toUpperCase() === subscaleKey);

  return matchedEntry?.[1];
};

const normalizeRiskLevel = (level) => {
  const normalized = (level ?? '').toLowerCase().trim();

  if (normalized === 'medium' || normalized.includes('moderate') || normalized.includes('中')) {
    return 'moderate';
  }

  if (normalized.includes('high') || normalized.includes('高')) {
    return 'high';
  }

  if (normalized.includes('low') || normalized.includes('低')) {
    return 'low';
  }

  return normalized;
};

const isLowRiskLevel = (level) => normalizeRiskLevel(level) === 'low';
const isModerateRiskLevel = (level) => normalizeRiskLevel(level) === 'moderate';
const isHighRiskLevel = (level) => normalizeRiskLevel(level) === 'high';
const isModerateOrAboveRiskLevel = (level) => isModerateRiskLevel(level) || isHighRiskLevel(level);

const isAllModerateOrAboveRisk = (riskLevels) => SDQ_SUBSCALE_ORDER.every((key) => isModerateOrAboveRiskLevel(getSubscaleRiskLevel(riskLevels, key)));

/* ------------------------------------------------------------------ *
 * score-based subscale picks
 * ------------------------------------------------------------------ */

const getScoredSubscales = (scoreSummary) =>
  SDQ_SUBSCALE_ORDER.map((key) => ({
    key,
    score: getScoreFromSummary(scoreSummary, key),
  }));

const getLowestScoreSubscaleKeys = (scoreSummary) => {
  const scored = getScoredSubscales(scoreSummary);
  const minScore = Math.min(...scored.map((item) => item.score));

  return scored.filter((item) => item.score === minScore).map((item) => item.key);
};

const getHighestScoreSubscaleKeys = (scoreSummary) => {
  const scored = getScoredSubscales(scoreSummary);
  const maxScore = Math.max(...scored.map((item) => item.score));

  return scored.filter((item) => item.score === maxScore).map((item) => item.key);
};

const excludeSubscaleKeys = (keys, excluded) => {
  if (excluded.length === 0) {
    return keys;
  }

  const excludedSet = new Set(excluded);

  return keys.filter((key) => !excludedSet.has(key));
};

/* ------------------------------------------------------------------ *
 * child_behavior_issues
 * ------------------------------------------------------------------ */

/** An issue is either a bare string or `{ label }` depending on the analysis version. */
export const normalizeBehaviorLabel = (issue) => {
  if (typeof issue === 'string') {
    return issue.trim();
  }

  return (issue?.label ?? '').trim();
};

/** The newer payload groups issues per subscale; the older one is a flat list of labels. */
const isGroupedSdqSurveyFormat = (entries) => entries.some((item) => (item.issues?.length ?? 0) > 0);

const getFlatBehaviorLabels = (childBehaviorIssues) =>
  (childBehaviorIssues?.sdq_survey ?? [])
    .map((item) => (item.label ?? '').trim())
    .filter(Boolean)
    .slice(0, MAX_RISK_SECTION_BEHAVIORS);

const getSurveyEntryForSubscale = (childBehaviorIssues, subscaleKey) =>
  (childBehaviorIssues?.sdq_survey ?? []).find((item) => item.subscale?.toUpperCase() === subscaleKey);

const getBehaviorsForSubscale = (childBehaviorIssues, subscaleKey) => {
  const entries = childBehaviorIssues?.sdq_survey ?? [];
  if (entries.length === 0) {
    return [];
  }

  if (isGroupedSdqSurveyFormat(entries)) {
    const surveyEntry = getSurveyEntryForSubscale(childBehaviorIssues, subscaleKey);

    return (surveyEntry?.issues ?? []).map(normalizeBehaviorLabel).filter(Boolean).slice(0, MAX_RISK_SECTION_BEHAVIORS);
  }

  return getFlatBehaviorLabels(childBehaviorIssues);
};

/** Flattens either payload shape into `{ subscale, label, issues[] }` groups for the legacy block. */
export const groupSdqSurveyBehaviorsForDisplay = (childBehaviorIssues) => {
  const entries = childBehaviorIssues?.sdq_survey ?? [];
  if (entries.length === 0) {
    return [];
  }

  if (isGroupedSdqSurveyFormat(entries)) {
    return entries.map((entry) => ({
      subscale: entry.subscale ?? '',
      label: entry.label,
      issues: (entry.issues ?? []).map(normalizeBehaviorLabel).filter(Boolean),
    }));
  }

  const issues = getFlatBehaviorLabels(childBehaviorIssues);
  if (issues.length === 0) {
    return [];
  }

  return [{ subscale: '', issues: issues }];
};

const getSubscaleLabelFromSurvey = (childBehaviorIssues, subscaleKey) => {
  const label = getSurveyEntryForSubscale(childBehaviorIssues, subscaleKey)?.label?.trim();

  return label || undefined;
};

/* ------------------------------------------------------------------ *
 * analysis_summary
 * ------------------------------------------------------------------ */

const getAnalysisSummaryItem = (analysisSummary, subscaleKey) => {
  if (!analysisSummary) {
    return undefined;
  }

  const directItem = analysisSummary[subscaleKey];
  if (directItem) {
    return directItem;
  }

  const matchedEntry = Object.entries(analysisSummary).find(([key]) => key.toUpperCase() === subscaleKey);

  return matchedEntry?.[1];
};

const joinCharacteristicHtml = (analysisSummary, keys) =>
  keys
    .map((key) => getAnalysisSummaryItem(analysisSummary, key)?.characteristic?.trim() ?? '')
    .filter(Boolean)
    .join('<br />');

const buildRiskDetailItems = (analysisResults, targetKeys) => {
  const { analysis_summary: analysisSummary, child_behavior_issues: childBehaviorIssues } = analysisResults;

  return targetKeys.map((key) => ({
    subscaleKey: key,
    subscaleLabel: getSubscaleLabelFromSurvey(childBehaviorIssues, key),
    characteristicHtml: getAnalysisSummaryItem(analysisSummary, key)?.characteristic?.trim() ?? '',
    solutionHtml: getAnalysisSummaryItem(analysisSummary, key)?.solution?.trim() ?? '',
    behaviors: getBehaviorsForSubscale(childBehaviorIssues, key),
  }));
};

/* ------------------------------------------------------------------ *
 * format detection / section builders
 * ------------------------------------------------------------------ */

/**
 * Both `analysis_summary` and `risk_levels` are required: gating on `risk_levels`
 * alone would render empty new sections next to the legacy summary.
 */
export const hasNewSdqAnalysisFormat = (analysisResults) => {
  if (!analysisResults) {
    return false;
  }

  const { analysis_summary: analysisSummary, risk_levels: riskLevels } = analysisResults;

  return (
    typeof analysisSummary === 'object' &&
    analysisSummary !== null &&
    Object.keys(analysisSummary).length > 0 &&
    typeof riskLevels === 'object' &&
    riskLevels !== null &&
    Object.keys(riskLevels).length > 0
  );
};

export const isSdqAllLowRisk = (analysisResults) => {
  if (!analysisResults?.risk_levels) {
    return false;
  }

  return SDQ_SUBSCALE_ORDER.every((key) => isLowRiskLevel(getSubscaleRiskLevel(analysisResults.risk_levels, key)));
};

export const isSdqBr07AllModerateOrAbove = (analysisResults) => {
  if (!analysisResults?.risk_levels) {
    return false;
  }

  const hasLowRisk = SDQ_SUBSCALE_ORDER.some((key) => isLowRiskLevel(getSubscaleRiskLevel(analysisResults.risk_levels, key)));

  return !hasLowRisk && isAllModerateOrAboveRisk(analysisResults.risk_levels);
};

export const getSdqReportDisplayCase = (analysisResults) => {
  if (!analysisResults?.risk_levels) {
    return null;
  }

  if (isSdqAllLowRisk(analysisResults)) {
    return SDQ_REPORT_DISPLAY_CASE.ALL_LOW;
  }

  if (isSdqBr07AllModerateOrAbove(analysisResults)) {
    return SDQ_REPORT_DISPLAY_CASE.BR07;
  }

  return SDQ_REPORT_DISPLAY_CASE.MIXED;
};

/** 上手にできていること - low-risk subscales (minus the weakest one when every subscale is low). */
export const buildStrengthsSectionData = (analysisResults) => {
  if (!hasNewSdqAnalysisFormat(analysisResults)) {
    return null;
  }

  const displayCase = getSdqReportDisplayCase(analysisResults);
  if (displayCase === SDQ_REPORT_DISPLAY_CASE.BR07) {
    return null;
  }

  const { risk_levels: riskLevels, analysis_summary: analysisSummary, score_summary: scoreSummary } = analysisResults;
  let strengthKeys = SDQ_SUBSCALE_ORDER.filter((key) => isLowRiskLevel(getSubscaleRiskLevel(riskLevels, key)));

  if (displayCase === SDQ_REPORT_DISPLAY_CASE.ALL_LOW) {
    if (!scoreSummary || Object.keys(scoreSummary).length === 0) {
      return null;
    }

    strengthKeys = excludeSubscaleKeys(strengthKeys, getLowestScoreSubscaleKeys(scoreSummary));
  }

  if (strengthKeys.length === 0) {
    return null;
  }

  const items = [];

  for (const key of strengthKeys) {
    const html = getAnalysisSummaryItem(analysisSummary, key)?.characteristic?.trim();
    if (html) {
      items.push({ subscaleKey: key, html: html });
    }
  }

  if (items.length === 0) {
    return null;
  }

  return { items: items, introSubscaleKeys: strengthKeys };
};

/** 頑張っていること for the all-low case - the single weakest subscale. */
export const buildAllLowSection6Data = (analysisResults) => {
  if (!hasNewSdqAnalysisFormat(analysisResults)) {
    return null;
  }

  if (getSdqReportDisplayCase(analysisResults) !== SDQ_REPORT_DISPLAY_CASE.ALL_LOW) {
    return null;
  }

  const { score_summary: scoreSummary } = analysisResults;
  if (!scoreSummary || Object.keys(scoreSummary).length === 0) {
    return null;
  }

  const targetKeys = getLowestScoreSubscaleKeys(scoreSummary);
  if (targetKeys.length === 0) {
    return null;
  }

  const items = buildRiskDetailItems(analysisResults, targetKeys);
  if (items.every((item) => !item.characteristicHtml && !item.solutionHtml && item.behaviors.length === 0)) {
    return null;
  }

  return { items: items };
};

/** 頑張っていること for the mixed case - the moderate-risk subscales. */
export const buildWorkingHardSectionData = (analysisResults) => {
  if (!hasNewSdqAnalysisFormat(analysisResults)) {
    return null;
  }

  if (getSdqReportDisplayCase(analysisResults) !== SDQ_REPORT_DISPLAY_CASE.MIXED) {
    return null;
  }

  const { risk_levels: riskLevels } = analysisResults;
  const targetKeys = SDQ_SUBSCALE_ORDER.filter((key) => isModerateRiskLevel(getSubscaleRiskLevel(riskLevels, key)));

  if (targetKeys.length === 0) {
    return null;
  }

  return { items: buildRiskDetailItems(analysisResults, targetKeys) };
};

/** 頑張っていること for the BR07 case - characteristics of the highest-scoring subscales only. */
export const buildBr07SectionData = (analysisResults) => {
  if (!hasNewSdqAnalysisFormat(analysisResults)) {
    return null;
  }

  if (getSdqReportDisplayCase(analysisResults) !== SDQ_REPORT_DISPLAY_CASE.BR07) {
    return null;
  }

  const { analysis_summary: analysisSummary, score_summary: scoreSummary } = analysisResults;
  if (!scoreSummary || Object.keys(scoreSummary).length === 0) {
    return null;
  }

  const targetKeys = getHighestScoreSubscaleKeys(scoreSummary);
  if (targetKeys.length === 0) {
    return null;
  }

  const characteristicHtml = joinCharacteristicHtml(analysisSummary, targetKeys);
  if (!characteristicHtml) {
    return null;
  }

  return { introSubscaleKeys: targetKeys, characteristicHtml: characteristicHtml };
};

/** これからのびる力 - the high-risk subscales (mixed and BR07 cases). */
export const buildFutureStrengthSectionData = (analysisResults) => {
  if (!hasNewSdqAnalysisFormat(analysisResults)) {
    return null;
  }

  const displayCase = getSdqReportDisplayCase(analysisResults);
  if (displayCase !== SDQ_REPORT_DISPLAY_CASE.MIXED && displayCase !== SDQ_REPORT_DISPLAY_CASE.BR07) {
    return null;
  }

  const { risk_levels: riskLevels } = analysisResults;
  const targetKeys = SDQ_SUBSCALE_ORDER.filter((key) => isHighRiskLevel(getSubscaleRiskLevel(riskLevels, key)));

  if (targetKeys.length === 0) {
    return null;
  }

  return { items: buildRiskDetailItems(analysisResults, targetKeys) };
};
